/**
 * The cosmetics, economy and progression contract.
 *
 * Declared before any implementation so the item model, the garage renderer and
 * the shop/pass/challenge systems can be built against one another — the same
 * discipline src/physics/surface.ts, src/rl/types.ts and src/net/protocol.ts
 * established.
 *
 * ===========================================================================
 * THIS MIRRORS THE REAL GAME, AND WHERE IT DOES NOT, IT SAYS SO
 * ===========================================================================
 *
 * Slots, rarities, painted variants, certifications, the Rocket Pass tier
 * economy and the Weekly/Season/Event challenge split are all taken from
 * Rocket League as it actually ships (sources in docs/COSMETICS_SOURCES.md).
 * Two slots are OURS and are marked `[ours]` below — a car number and a country
 * flag. Rocket League has no such slots; it expresses that identity through
 * Player Banners and Decals. They are included because they were asked for, and
 * flagged so nobody later "fixes" the game to match a wiki and deletes them.
 *
 * ===========================================================================
 * EVERYTHING IS PROCEDURAL
 * ===========================================================================
 *
 * There are no asset files in this project and none are being added. Every
 * decal, flag, number, wheel, topper and trail is generated at runtime from
 * code. That is a real constraint on what an item can BE: an item is a
 * generator id plus parameters, never a path to a texture.
 *
 * ===========================================================================
 * NOTHING HERE IS PURCHASABLE WITH REAL MONEY
 * ===========================================================================
 *
 * Credits are earned in-game only. There is no payment path, no currency
 * purchase and no external transaction anywhere in this codebase, and none
 * should be added without the user asking for it explicitly. The Rocket Pass
 * "premium" track is unlocked with earned Credits, which keeps the structure of
 * the real thing without the commerce.
 */

import type { CarBody } from '../physics/car-config';

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

/**
 * Every customization slot. The first thirteen are Rocket League's own set;
 * `number` and `flag` are ours.
 */
export type CosmeticSlot =
  | 'body'
  | 'decal'
  | 'paintFinish'
  | 'wheels'
  | 'boost'
  | 'topper'
  | 'antenna'
  | 'trail'
  | 'goalExplosion'
  | 'engineAudio'
  | 'banner'
  | 'title'
  | 'avatarBorder'
  /** [ours] A racing number displayed on the car. */
  | 'number'
  /** [ours] A country flag displayed on the car. */
  | 'flag';

export const COSMETIC_SLOTS: readonly CosmeticSlot[] = [
  'body', 'decal', 'paintFinish', 'wheels', 'boost', 'topper', 'antenna',
  'trail', 'goalExplosion', 'engineAudio', 'banner', 'title', 'avatarBorder',
  'number', 'flag',
];

/** Slots whose item is drawn on or attached to the car itself. */
export const VISUAL_CAR_SLOTS: readonly CosmeticSlot[] = [
  'body', 'decal', 'paintFinish', 'wheels', 'boost', 'topper', 'antenna',
  'trail', 'number', 'flag',
];

// ---------------------------------------------------------------------------
// Rarity, paint, certification
// ---------------------------------------------------------------------------

/** Rocket League's ladder, in order. `limited` is the event-only tier. */
export type Rarity =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'veryRare'
  | 'import'
  | 'exotic'
  | 'blackMarket'
  | 'limited';

export const RARITY_ORDER: readonly Rarity[] = [
  'common', 'uncommon', 'rare', 'veryRare', 'import', 'exotic', 'blackMarket', 'limited',
];

/**
 * The thirteen painted variants. Titanium White is deliberately last: in the
 * real game it is the one everybody wants, and the drop tables below should
 * treat it as the rarest roll rather than as just another colour.
 */
export type PaintColor =
  | 'crimson' | 'skyBlue' | 'pink' | 'cobalt' | 'burntSienna' | 'forestGreen'
  | 'purple' | 'saffron' | 'lime' | 'grey' | 'black' | 'white' | 'titaniumWhite';

export const PAINT_COLORS: readonly PaintColor[] = [
  'crimson', 'skyBlue', 'pink', 'cobalt', 'burntSienna', 'forestGreen',
  'purple', 'saffron', 'lime', 'grey', 'black', 'white', 'titaniumWhite',
];

/**
 * Certifications — stat trackers that count a specific action on the item that
 * carries them. Each maps to a match statistic the simulation already produces
 * or can produce; an implementation must not invent one it cannot count.
 */
export type Certification =
  | 'striker' | 'scorer' | 'sniper' | 'goalkeeper' | 'guardian' | 'playmaker'
  | 'tactician' | 'acrobat' | 'showOff' | 'juggler' | 'sweeper' | 'turtle'
  | 'aviator' | 'paragon' | 'victor';

export const CERTIFICATIONS: readonly Certification[] = [
  'striker', 'scorer', 'sniper', 'goalkeeper', 'guardian', 'playmaker',
  'tactician', 'acrobat', 'showOff', 'juggler', 'sweeper', 'turtle',
  'aviator', 'paragon', 'victor',
];

// ---------------------------------------------------------------------------
// Items
// ---------------------------------------------------------------------------

/**
 * A catalogue entry. Immutable, shipped with the build, addressed by `id`.
 *
 * `generator` + `params` is what makes this work without assets: the renderer
 * looks up a procedural generator by name and feeds it the parameters. An item
 * is a recipe, not a file.
 */
export interface ItemDef {
  readonly id: string;
  readonly slot: CosmeticSlot;
  readonly name: string;
  readonly rarity: Rarity;
  /** Restricted to these bodies, or undefined for universal. */
  readonly bodies?: readonly CarBody[];
  /** Can this item roll a painted variant? */
  readonly paintable: boolean;
  /** Can this item roll a certification? */
  readonly certifiable: boolean;
  /** Name of the procedural generator that draws it. */
  readonly generator: string;
  readonly params?: Readonly<Record<string, number | string | boolean>>;
  /** Not obtainable from drops; event or pass only. */
  readonly exclusive?: boolean;
}

/** A specific owned copy: a catalogue item plus its rolled attributes. */
export interface InventoryItem {
  readonly instanceId: string;
  readonly itemId: string;
  readonly paint?: PaintColor;
  readonly certification?: Certification;
  /** Counter for the certification, if any. */
  certValue?: number;
  /** When it entered the inventory, for "New" badges and sorting. */
  readonly acquiredAt: number;
}

/** What a player has equipped, per team colour — Rocket League keeps two. */
export interface Loadout {
  /** Instance ids, or null for "nothing equipped". */
  readonly slots: Readonly<Partial<Record<CosmeticSlot, string | null>>>;
  /** Primary and accent paint, independent of any painted item. */
  readonly primaryColor: string;
  readonly accentColor: string;
}

export interface Preset {
  readonly id: string;
  name: string;
  readonly blue: Loadout;
  readonly orange: Loadout;
}

// ---------------------------------------------------------------------------
// Economy
// ---------------------------------------------------------------------------

/**
 * Credits are EARNED ONLY. See the file header — there is deliberately no
 * purchase path anywhere in this codebase.
 */
export interface Wallet {
  credits: number;
}

/** A blueprint: a known item you may build for a price, as the real game does. */
export interface Blueprint {
  readonly instanceId: string;
  readonly itemId: string;
  readonly paint?: PaintColor;
  readonly cost: number;
  readonly acquiredAt: number;
}

/** Five of one rarity in, one of the next out — Rocket League's trade-in. */
export const TRADE_IN_COUNT = 5;

// ---------------------------------------------------------------------------
// Progression
// ---------------------------------------------------------------------------

export interface PlayerProgress {
  xp: number;
  level: number;
  /** Tier points toward the current Rocket Pass tier. */
  tierPoints: number;
  passTier: number;
  passPremium: boolean;
  /** Lifetime stats, which also feed certifications and challenges. */
  stats: MatchStats;
}

/**
 * The statistics a match produces. Every challenge condition and every
 * certification must be expressible in terms of these — if a new one is needed,
 * it goes here first so there is exactly one definition of "a save".
 */
export interface MatchStats {
  matches: number;
  wins: number;
  goals: number;
  assists: number;
  saves: number;
  shots: number;
  demolitions: number;
  mvps: number;
  ownGoals: number;
  aerialGoals: number;
  backwardsGoals: number;
  longGoals: number;
  /** Total distance driven, uu. */
  distance: number;
  /** Seconds of match time played. */
  secondsPlayed: number;
  /** Ball touches. */
  touches: number;
  /** Boost pads collected. */
  boostCollected: number;
}

/** Rocket Pass: 70 tiers, then Pro Tiers. 10 Tier Points advance one tier. */
export const PASS_TIERS = 70;
export const TIER_POINTS_PER_TIER = 10;
/** An XP level grants this many Tier Points, as in the real game. */
export const TIER_POINTS_PER_LEVEL = 10;
/** Challenge difficulty -> Tier Points, mirroring Rocket League's 3/5/8. */
export const CHALLENGE_TIER_POINTS = { easy: 3, medium: 5, hard: 8 } as const;
/** Credits to unlock the premium track. Earned in-game; see the header. */
export const PASS_PREMIUM_COST = 1000;

// ---------------------------------------------------------------------------
// Challenges
// ---------------------------------------------------------------------------

export type ChallengeKind = 'weekly' | 'season' | 'event';
export type ChallengeDifficulty = keyof typeof CHALLENGE_TIER_POINTS;

/**
 * A challenge is a counter over `MatchStats` plus a target. Keeping the
 * condition to a stat key and a threshold — rather than an arbitrary predicate
 * — is what lets progress be computed from a match summary, persisted, and
 * shown as "3 / 10" without the challenge system knowing what a goal is.
 */
export interface ChallengeDef {
  readonly id: string;
  readonly kind: ChallengeKind;
  readonly difficulty: ChallengeDifficulty;
  readonly name: string;
  readonly description: string;
  readonly stat: keyof MatchStats;
  readonly target: number;
  /** Only counts in these modes, or undefined for any. */
  readonly modes?: readonly string[];
  /** Requires the premium pass track. */
  readonly premiumOnly?: boolean;
  readonly reward: ChallengeReward;
}

export type ChallengeReward =
  | { kind: 'xp'; amount: number }
  | { kind: 'credits'; amount: number }
  | { kind: 'item'; itemId: string }
  | { kind: 'drop'; rarity: Rarity };

export interface ChallengeProgress {
  readonly id: string;
  value: number;
  claimed: boolean;
  /** Epoch ms when this challenge expires; weeklies roll over. */
  readonly expiresAt: number;
}

/**
 * Weeklies refresh on Wednesday in the real game, and three free challenges are
 * joined by three more for premium holders.
 */
export const WEEKLY_REFRESH_DAY = 3; // 0 = Sunday
export const WEEKLY_FREE_COUNT = 3;
export const WEEKLY_PREMIUM_COUNT = 3;
/** Season challenges arrive in four stages, as Rocket League's do. */
export const SEASON_STAGES = 4;

// ---------------------------------------------------------------------------
// Shop
// ---------------------------------------------------------------------------

/**
 * The shop rotates. Rotation MUST be a pure function of the calendar day and a
 * fixed seed, never `Math.random`: a shop that re-rolls on refresh is a shop a
 * player can reroll by reloading, and it also cannot be tested.
 */
export interface ShopRotation {
  /** Days since epoch this rotation is for. */
  readonly day: number;
  readonly featured: readonly ShopEntry[];
  readonly daily: readonly ShopEntry[];
}

export interface ShopEntry {
  readonly itemId: string;
  readonly paint?: PaintColor;
  readonly price: number;
  /** Struck-through original, for a sale. */
  readonly wasPrice?: number;
}

export const SHOP_FEATURED_COUNT = 4;
export const SHOP_DAILY_COUNT = 6;

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------

/**
 * Everything the player owns and has earned. Persisted to localStorage under
 * its own key, versioned separately from `GameSettings` so a cosmetics
 * migration cannot cost somebody their key bindings.
 */
export interface PlayerProfile {
  version: number;
  displayName: string;
  wallet: Wallet;
  progress: PlayerProgress;
  inventory: InventoryItem[];
  blueprints: Blueprint[];
  presets: Preset[];
  activePresetId: string;
  challenges: ChallengeProgress[];
  /** Days-since-epoch of the last shop the player saw, for "new" badges. */
  lastShopDay: number;
}

export const PROFILE_VERSION = 1;
